import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    alpha,
    Alert,
    AppBar,
    Box,
    Button,
    CircularProgress,
    Container,
    CssBaseline,
    Grid,
    IconButton,
    Link,
    Paper,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useThemeMode } from "../../theme/ThemeModeProvider";
import { createAppTheme } from "../../theme/appTheme";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookIcon from "@mui/icons-material/Facebook";
import LoginIcon from "@mui/icons-material/Login";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { fetchSchoolPage } from "../../redux/schoolPageRelated/schoolPageHandle";
import { resolveUploadedMediaUrl } from "../../config/api";
import {
    looksLikeInternalSchoolId,
    publicSchoolApplyPath,
    publicSchoolLoginPath,
    publicSchoolPath,
    publicSchoolUrl,
} from "../../utils/schoolSlug";
import { APP_NAME, PLATFORM_LOGIN_LABEL } from "../../config/brand";
import { isSchoolSubdomainsEnabled } from "../../config/platformHost";
import { isSubscriptionActive } from "../../config/subscriptionPlans";
import PublicSchoolSubscriptionOverlay from "../../components/subscription/PublicSchoolSubscriptionOverlay";

const PAGE_BG = "#0a0b1a";
const SURFACE = "#12142a";
const SURFACE_ELEV = "#1a1d38";
const DEFAULT_ORANGE = "#ff7a00";
const MUTED = "#8b93a7";

const TOPIC_IMAGES = [
    "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=640&q=80",
    "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=640&q=80",
    "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=640&q=80",
    "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=640&q=80",
];

const HERO_FALLBACK =
    "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&q=80";

function parseKeyFacts(schoolPage) {
    const raw = schoolPage?.keyFacts;
    if (Array.isArray(raw)) return raw.filter((f) => f?.label || f?.value);
    try {
        const parsed = JSON.parse(raw || "[]");
        return Array.isArray(parsed) ? parsed.filter((f) => f?.label || f?.value) : [];
    } catch {
        return [];
    }
}

function formatNoticeDate(value) {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

function whatsAppHref(number) {
    const digits = String(number || "").replace(/\D/g, "");
    return digits ? `https://wa.me/${digits}` : "";
}

function scrollToId(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

function SchoolLoginButton({ slug, size = "medium", variant = "contained", sx, fullWidth = false }) {
    const navigate = useNavigate();
    if (!slug) return null;
    const btnSize = size === "large" ? "large" : size === "small" ? "small" : "medium";
    return (
        <Button
            variant={variant}
            size={btnSize}
            fullWidth={fullWidth}
            startIcon={<LoginIcon />}
            onClick={() => navigate(publicSchoolLoginPath(slug))}
            sx={sx}
        >
            School login
        </Button>
    );
}

function NavLink({ children, href }) {
    return (
        <Button
            component="a"
            href={href}
            color="inherit"
            sx={{
                color: MUTED,
                fontWeight: 600,
                fontSize: 14,
                textTransform: "none",
                minWidth: "auto",
                px: 1.25,
                "&:hover": { color: "common.white", bgcolor: alpha("#fff", 0.06) },
            }}
        >
            {children}
        </Button>
    );
}

/**
 * @param {{ schoolIdProp?: string, schoolSlugFromHost?: string, isRootPlatformHome?: boolean }} props
 * When `schoolIdProp` or `schoolSlugFromHost` is set, the page loads that school without a `:slug` route param.
 */
function SchoolPublicPage({ schoolIdProp, schoolSlugFromHost, isRootPlatformHome = false }) {
    const { slug: slugFromRoute } = useParams();
    const location = useLocation();
    const routeRef = schoolSlugFromHost || schoolIdProp || slugFromRoute;
    const [searchParams] = useSearchParams();
    const isPreview = searchParams.get("preview") === "1";
    const { mode } = useThemeMode();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { schoolPage, loading } = useSelector((s) => s.schoolPage);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    useEffect(() => {
        if (routeRef) {
            dispatch(fetchSchoolPage(routeRef));
        }
    }, [dispatch, routeRef]);

    const publicSlug = schoolPage?.publicSlug || slugFromRoute;
    const schoolId = schoolPage?.schoolId || schoolPage?._id;

    useEffect(() => {
        if (schoolSlugFromHost || schoolIdProp || !schoolPage?.publicSlug || !slugFromRoute) return;
        const canonical = schoolPage.publicSlug;
        if (slugFromRoute === canonical) return;
        if (looksLikeInternalSchoolId(slugFromRoute) || slugFromRoute === schoolPage.schoolId) {
            if (isSchoolSubdomainsEnabled()) {
                const dest = publicSchoolUrl(canonical);
                if (dest && /^https?:\/\//i.test(dest)) {
                    window.location.replace(`${dest}${location.search}`);
                    return;
                }
            }
            navigate(`${publicSchoolPath(canonical)}${location.search}`, { replace: true });
        }
    }, [schoolPage, slugFromRoute, schoolIdProp, schoolSlugFromHost, navigate, location.search]);

    const pageTheme = useMemo(() => createAppTheme(mode), [mode]);

    useEffect(() => {
        if (schoolPage?.schoolName) {
            document.title = `${schoolPage.schoolName} | ${APP_NAME}`;
        }
        return () => {
            document.title = APP_NAME;
        };
    }, [schoolPage?.schoolName]);

    const facts = useMemo(() => (schoolPage ? parseKeyFacts(schoolPage) : []), [schoolPage]);
    const notices = Array.isArray(schoolPage?.notices) ? schoolPage.notices.slice(0, 3) : [];

    const heroImage = useMemo(() => {
        const raw = schoolPage?.heroImageUrl?.trim();
        if (!raw) return HERO_FALLBACK;
        return resolveUploadedMediaUrl(raw) || raw;
    }, [schoolPage?.heroImageUrl]);

    const brandOrange = useMemo(() => {
        const color = String(schoolPage?.accentColor ?? "").trim();
        return color || DEFAULT_ORANGE;
    }, [schoolPage?.accentColor]);

    const subscriptionBlocked =
        schoolPage && !isPreview && !isSubscriptionActive(schoolPage);
    const staffLoginPath = schoolId
        ? publicSchoolLoginPath(schoolId, publicSlug)
        : "/login";

    const featureCards = useMemo(() => {
        const fromFacts = facts.slice(0, 4).map((f) => ({
            title: f.label || "Highlights",
            text: f.value || "",
        }));
        const filler = [
            { title: "Dedicated educators", text: "Experienced staff committed to every learner’s growth and wellbeing." },
            { title: "Creative learning", text: "Engaging lessons that build confidence, curiosity, and real-world skills." },
            { title: "Strong community", text: "A welcoming environment where families and students feel supported." },
            { title: "Future focused", text: "Preparing students for the next academic step and beyond." },
        ];
        const out = [...fromFacts];
        let i = 0;
        while (out.length < 4 && i < filler.length) {
            out.push(filler[i]);
            i += 1;
        }
        return out.slice(0, 4);
    }, [facts]);

    const topicCards = useMemo(() => {
        const fromFacts = facts.slice(0, 4).map((f, i) => ({
            title: f.label || `Programme ${i + 1}`,
            subtitle: f.value || "Discover more",
            image: TOPIC_IMAGES[i % TOPIC_IMAGES.length],
        }));
        const defaults = [
            { title: "Academic excellence", subtitle: "Rigorous, caring instruction", image: TOPIC_IMAGES[0] },
            { title: "Holistic growth", subtitle: "Character & leadership", image: TOPIC_IMAGES[1] },
            { title: "Modern learning", subtitle: "Resources & support", image: TOPIC_IMAGES[2] },
            { title: "Community life", subtitle: "Activities & culture", image: TOPIC_IMAGES[3] },
        ];
        const merged = [...fromFacts];
        let j = 0;
        while (merged.length < 4) {
            merged.push(defaults[j % defaults.length]);
            j += 1;
        }
        return merged.slice(0, 4);
    }, [facts]);

    const heroPattern = {
        backgroundColor: PAGE_BG,
        backgroundImage: `
      radial-gradient(circle at 12% 22%, ${alpha(brandOrange, 0.12)} 0%, transparent 42%),
      radial-gradient(circle at 88% 8%, ${alpha("#7c3aed", 0.14)} 0%, transparent 38%),
      radial-gradient(circle at 70% 80%, ${alpha("#2563eb", 0.08)} 0%, transparent 36%),
      repeating-linear-gradient(135deg, ${alpha("#fff", 0.02)} 0, ${alpha("#fff", 0.02)} 1px, transparent 1px, transparent 14px)
    `,
    };

    const renderNavbar = () => (
        <AppBar
            position="sticky"
            elevation={0}
            sx={{
                bgcolor: alpha(SURFACE, 0.92),
                backdropFilter: "blur(12px)",
                borderBottom: `1px solid ${alpha("#fff", 0.06)}`,
            }}
        >
            <Toolbar sx={{ maxWidth: 1200, width: "100%", mx: "auto", px: { xs: 1.5, sm: 3 }, minHeight: { xs: 64, sm: 72 }, gap: 1 }}>
                <IconButton
                    color="inherit"
                    aria-label="Open menu"
                    onClick={() => setMobileNavOpen(true)}
                    sx={{ display: { md: "none" }, color: "common.white", mr: 0.5 }}
                >
                    <MenuIcon />
                </IconButton>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: { xs: 0, md: 2 }, minWidth: 0, flex: { xs: 1, md: "none" } }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: alpha(brandOrange, 0.15),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <SchoolIcon sx={{ color: brandOrange, fontSize: 24 }} />
                    </Box>
                    <Typography
                        variant="h6"
                        fontWeight={800}
                        noWrap
                        sx={{ color: "common.white", fontSize: { xs: "0.95rem", sm: "1.25rem" } }}
                    >
                        {schoolPage?.schoolName || "School"}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={0.5} sx={{ display: { xs: "none", md: "flex" }, flex: 1, justifyContent: "center" }}>
                    <NavLink href="#hero">Home</NavLink>
                    <NavLink href="#features">Highlights</NavLink>
                    <NavLink href="#about">About</NavLink>
                    <NavLink href="#topics">Topics</NavLink>
                    <NavLink href="#apply">Apply</NavLink>
                    {schoolPage?.showAnnouncements && notices.length > 0 ? <NavLink href="#news">News</NavLink> : null}
                    <NavLink href="#contact">Contact</NavLink>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <IconButton size="small" sx={{ color: MUTED, display: { xs: "none", sm: "inline-flex" } }} aria-label="Search">
                        <SearchIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ color: MUTED, display: { xs: "none", sm: "inline-flex" } }} aria-label="Account">
                        <PersonOutlineIcon fontSize="small" />
                    </IconButton>
                    <Button
                        variant="contained"
                        onClick={() => scrollToId("contact")}
                        sx={{
                            ml: 1,
                            px: { xs: 1.5, sm: 2.5 },
                            borderRadius: 999,
                            bgcolor: brandOrange,
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            boxShadow: `0 8px 28px ${alpha(brandOrange, 0.35)}`,
                            "&:hover": { bgcolor: alpha(brandOrange, 0.92) },
                        }}
                    >
                        Get in Touch
                    </Button>
                    {!isSchoolSubdomainsEnabled() ? (
                        <Button
                            component={RouterLink}
                            to="/login"
                            size="small"
                            sx={{
                                ml: 0.5,
                                display: { xs: "none", md: "inline-flex" },
                                color: MUTED,
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: 999,
                                "&:hover": { color: "common.white", bgcolor: alpha("#fff", 0.06) },
                            }}
                        >
                            {PLATFORM_LOGIN_LABEL}
                        </Button>
                    ) : null}
                    <SchoolLoginButton
                        slug={publicSlug}
                        size="small"
                        variant="outlined"
                        sx={{
                            ml: 0.5,
                            display: { xs: "inline-flex", sm: "inline-flex" },
                            borderColor: alpha("#fff", 0.25),
                            color: "common.white",
                            borderRadius: 999,
                            fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                            px: { xs: 1, sm: 1.5 },
                            "&:hover": { borderColor: alpha("#fff", 0.45), bgcolor: alpha("#fff", 0.06) },
                        }}
                    />
                </Stack>
            </Toolbar>
            <Drawer
                anchor="right"
                open={mobileNavOpen}
                onClose={() => setMobileNavOpen(false)}
                PaperProps={{
                    sx: {
                        width: "min(300px, 88vw)",
                        bgcolor: SURFACE,
                        color: "common.white",
                        p: 2,
                    },
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography fontWeight={800}>Menu</Typography>
                    <IconButton onClick={() => setMobileNavOpen(false)} aria-label="Close menu" sx={{ color: "common.white" }}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
                <Stack spacing={1}>
                    {["hero", "features", "about", "topics", "apply", ...(schoolPage?.showAnnouncements && notices.length > 0 ? ["news"] : []), "contact"].map(
                        (id) => (
                            <Button
                                key={id}
                                fullWidth
                                onClick={() => {
                                    if (id === "apply") {
                                        navigate(publicSchoolApplyPath(publicSlug));
                                        setMobileNavOpen(false);
                                        return;
                                    }
                                    scrollToId(id);
                                    setMobileNavOpen(false);
                                }}
                                sx={{ justifyContent: "flex-start", color: MUTED, textTransform: "capitalize" }}
                            >
                                {id === "hero" ? "Home" : id === "apply" ? "Apply for admission" : id}
                            </Button>
                        ),
                    )}
                    {!isSchoolSubdomainsEnabled() ? (
                        <Button component={RouterLink} to="/login" fullWidth onClick={() => setMobileNavOpen(false)}>
                            {PLATFORM_LOGIN_LABEL}
                        </Button>
                    ) : null}
                    <SchoolLoginButton
                        slug={publicSlug}
                        fullWidth
                        sx={{ bgcolor: brandOrange, borderRadius: 999 }}
                    />
                </Stack>
            </Drawer>
        </AppBar>
    );

    const renderComingSoon = () => (
        <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", px: 2, py: 8 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    maxWidth: 480,
                    textAlign: "center",
                    borderRadius: 4,
                    bgcolor: SURFACE_ELEV,
                    border: `1px solid ${alpha("#fff", 0.08)}`,
                }}
            >
                <SchoolOutlinedIcon sx={{ fontSize: 64, mb: 2, color: brandOrange }} />
                <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: "common.white" }}>
                    {schoolPage?.schoolName || "This school"}
                </Typography>
                <Typography sx={{ color: MUTED, mb: 3 }}>
                    This school&apos;s public page is coming soon.
                </Typography>
                <SchoolLoginButton slug={publicSlug} size="large" sx={{ bgcolor: brandOrange, borderRadius: 999 }} />
            </Paper>
        </Box>
    );

    const renderFullPage = () => {
        const headline = schoolPage.heroHeadline?.trim() || schoolPage.schoolName;
        const tagline =
            schoolPage.heroTagline?.trim() ||
            "A better learning journey starts here — discover programmes, meet our community, and take the next step with us.";

        return (
            <>
                {renderNavbar()}

                <Box id="hero" sx={{ ...heroPattern, pt: { xs: 6, md: 10 }, pb: { xs: 8, md: 12 } }}>
                    <Container maxWidth="lg">
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography
                                    variant="overline"
                                    sx={{ color: alpha(brandOrange, 0.95), letterSpacing: 2, fontWeight: 700 }}
                                >
                                    Welcome
                                </Typography>
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    sx={{
                                        color: "common.white",
                                        mt: 1,
                                        mb: 2,
                                        fontSize: { xs: "2rem", sm: "2.65rem", md: "3.1rem" },
                                        lineHeight: 1.15,
                                    }}
                                >
                                    {headline}
                                </Typography>
                                <Typography sx={{ color: MUTED, maxWidth: 520, mb: 3, lineHeight: 1.7 }}>
                                    {tagline}
                                </Typography>
                                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 4 }}>
                                    {schoolPage.heroCtaLink ? (
                                        <Button
                                            component="a"
                                            href={schoolPage.heroCtaLink}
                                            variant="contained"
                                            endIcon={<ArrowForwardIcon />}
                                            sx={{
                                                px: 3,
                                                py: 1.25,
                                                borderRadius: 999,
                                                bgcolor: brandOrange,
                                                fontSize: 16,
                                                boxShadow: `0 12px 36px ${alpha(brandOrange, 0.35)}`,
                                                "&:hover": { bgcolor: alpha(brandOrange, 0.9) },
                                            }}
                                        >
                                            {schoolPage.heroCtaText || "Take now"}
                                        </Button>
                                    ) : (
                                        <Button
                                            component={RouterLink}
                                            to={publicSchoolApplyPath(publicSlug)}
                                            variant="contained"
                                            endIcon={<ArrowForwardIcon />}
                                            sx={{
                                                px: 3,
                                                py: 1.25,
                                                borderRadius: 999,
                                                bgcolor: brandOrange,
                                                fontSize: 16,
                                                boxShadow: `0 12px 36px ${alpha(brandOrange, 0.35)}`,
                                                "&:hover": { bgcolor: alpha(brandOrange, 0.9) },
                                            }}
                                        >
                                            {schoolPage.heroCtaText || "Apply for admission"}
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => scrollToId("topics")}
                                        sx={{
                                            px: 3,
                                            py: 1.25,
                                            borderRadius: 999,
                                            borderColor: alpha("#fff", 0.35),
                                            color: "common.white",
                                            fontSize: 16,
                                            "&:hover": { borderColor: "common.white", bgcolor: alpha("#fff", 0.06) },
                                        }}
                                    >
                                        Explore highlights
                                    </Button>
                                </Stack>

                                {schoolPage.showStats &&
                                (schoolPage.studentCount || schoolPage.classCount || schoolPage.teacherCount) ? (
                                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                        {[
                                            { value: schoolPage.studentCount, label: "Students" },
                                            { value: schoolPage.classCount, label: "Classes" },
                                            { value: schoolPage.teacherCount, label: "Teachers" },
                                        ].map(
                                            (stat) =>
                                                stat.value != null &&
                                                stat.value !== "" && (
                                                    <Paper
                                                        key={stat.label}
                                                        sx={{
                                                            px: 2.5,
                                                            py: 1.5,
                                                            bgcolor: SURFACE_ELEV,
                                                            border: `1px solid ${alpha("#fff", 0.06)}`,
                                                            borderRadius: 3,
                                                            minWidth: 120,
                                                        }}
                                                    >
                                                        <Typography variant="h5" fontWeight={800} sx={{ color: brandOrange }}>
                                                            {stat.value}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: MUTED }}>
                                                            {stat.label}
                                                        </Typography>
                                                    </Paper>
                                                )
                                        )}
                                    </Stack>
                                ) : null}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ position: "relative", maxWidth: 440, mx: { xs: "auto", md: "auto" } }}>
                                    <Box
                                        sx={{
                                            width: "100%",
                                            pt: "100%",
                                            borderRadius: "50%",
                                            position: "relative",
                                            background: `linear-gradient(135deg, ${brandOrange}, #7c3aed)`,
                                            p: "4px",
                                            boxShadow: `0 24px 80px ${alpha("#000", 0.45)}`,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: "4px",
                                                borderRadius: "50%",
                                                overflow: "hidden",
                                                bgcolor: SURFACE,
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={heroImage}
                                                alt=""
                                                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: "8%",
                                            right: "-4%",
                                            width: 52,
                                            height: 52,
                                            borderRadius: "50%",
                                            bgcolor: SURFACE_ELEV,
                                            border: `1px solid ${alpha("#fff", 0.1)}`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: `0 12px 32px ${alpha("#000", 0.35)}`,
                                        }}
                                    >
                                        <SchoolIcon sx={{ color: brandOrange }} />
                                    </Box>
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            bottom: "14%",
                                            left: "-6%",
                                            width: 56,
                                            height: 56,
                                            borderRadius: "50%",
                                            bgcolor: alpha(brandOrange, 0.95),
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            boxShadow: `0 12px 32px ${alpha(brandOrange, 0.4)}`,
                                        }}
                                        onClick={() => scrollToId("about")}
                                        role="button"
                                        aria-label="Learn about us"
                                    >
                                        <PlayCircleOutlineIcon sx={{ color: "#fff", fontSize: 34 }} />
                                    </Box>
                                    <Paper
                                        sx={{
                                            position: "absolute",
                                            bottom: "-2%",
                                            right: "4%",
                                            p: 2,
                                            maxWidth: 200,
                                            borderRadius: 3,
                                            bgcolor: SURFACE_ELEV,
                                            border: `1px solid ${alpha("#fff", 0.08)}`,
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ color: MUTED, fontWeight: 600 }}>
                                            Latest
                                        </Typography>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: "common.white", mt: 0.5 }}>
                                            {notices[0]?.title || "Open day & admissions"}
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={() => scrollToId("news")}
                                            sx={{ mt: 1, color: brandOrange, p: 0, minWidth: 0, fontWeight: 700 }}
                                        >
                                            Read more
                                        </Button>
                                    </Paper>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                <Box id="features" sx={{ py: { xs: 6, md: 8 }, bgcolor: PAGE_BG }}>
                    <Container maxWidth="lg">
                        <Grid container spacing={2.5}>
                            {featureCards.map((card, idx) => (
                                <Grid item xs={12} sm={6} md={3} key={idx}>
                                    <Paper
                                        sx={{
                                            height: "100%",
                                            p: 2.5,
                                            bgcolor: SURFACE_ELEV,
                                            border: `1px solid ${alpha("#fff", 0.06)}`,
                                            borderRadius: 3,
                                            transition: "transform 0.2s, box-shadow 0.2s",
                                            "&:hover": {
                                                transform: "translateY(-4px)",
                                                boxShadow: `0 16px 40px ${alpha("#000", 0.35)}`,
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2,
                                                bgcolor: alpha(brandOrange, 0.2),
                                                mb: 2,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <MenuBookOutlinedIcon sx={{ color: brandOrange }} />
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight={800} sx={{ color: "common.white", mb: 1 }}>
                                            {card.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: MUTED, mb: 2, minHeight: 48 }}>
                                            {card.text}
                                        </Typography>
                                        <Link
                                            component="button"
                                            type="button"
                                            onClick={() => scrollToId("about")}
                                            underline="hover"
                                            sx={{
                                                color: brandOrange,
                                                fontWeight: 700,
                                                fontSize: 14,
                                                cursor: "pointer",
                                                border: "none",
                                                background: "none",
                                                p: 0,
                                                font: "inherit",
                                            }}
                                        >
                                            Read more &gt;
                                        </Link>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>

                {schoolPage.showAbout && schoolPage.aboutBody ? (
                    <Box id="about" sx={{ py: { xs: 8, md: 10 }, bgcolor: SURFACE }}>
                        <Container maxWidth="lg">
                            <Grid container spacing={5} alignItems="center">
                                <Grid item xs={12} md={5}>
                                    <Box sx={{ position: "relative", maxWidth: 400, mx: "auto" }}>
                                        <Box
                                            sx={{
                                                width: "100%",
                                                pt: "100%",
                                                borderRadius: "50%",
                                                position: "relative",
                                                overflow: "hidden",
                                                border: `3px solid ${alpha(brandOrange, 0.45)}`,
                                                boxShadow: `0 24px 60px ${alpha("#000", 0.4)}`,
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={heroImage}
                                                alt=""
                                                sx={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </Box>
                                        {schoolPage.phone ? (
                                            <Paper
                                                sx={{
                                                    position: "absolute",
                                                    left: 16,
                                                    bottom: 16,
                                                    px: 2,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: SURFACE_ELEV,
                                                    border: `1px solid ${alpha("#fff", 0.1)}`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.5,
                                                }}
                                            >
                                                <PhoneIcon sx={{ color: brandOrange }} />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: MUTED }}>
                                                        Call us
                                                    </Typography>
                                                    <Typography
                                                        component="a"
                                                        href={`tel:${schoolPage.phone}`}
                                                        variant="body2"
                                                        fontWeight={700}
                                                        sx={{ color: "common.white", textDecoration: "none", display: "block" }}
                                                    >
                                                        {schoolPage.phone}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        ) : null}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={7}>
                                    <Typography variant="overline" sx={{ color: "#60a5fa", letterSpacing: 2, fontWeight: 700 }}>
                                        About us
                                    </Typography>
                                    <Typography variant="h3" sx={{ color: "common.white", mt: 1, mb: 2, fontSize: { xs: "1.75rem", md: "2.25rem" } }}>
                                        {schoolPage.aboutTitle || "Creating a lifelong learning community"}
                                    </Typography>
                                    <Typography sx={{ color: MUTED, whiteSpace: "pre-line", mb: 4, lineHeight: 1.75 }}>
                                        {schoolPage.aboutBody}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Stack direction="row" spacing={2}>
                                                <Box
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: 2,
                                                        bgcolor: alpha(brandOrange, 0.15),
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <MenuBookOutlinedIcon sx={{ color: brandOrange }} />
                                                </Box>
                                                <Box>
                                                    <Typography fontWeight={800} sx={{ color: "common.white" }}>
                                                        Flexible learning
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
                                                        Structured pathways that adapt to your child&apos;s strengths and goals.
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Stack direction="row" spacing={2}>
                                                <Box
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: 2,
                                                        bgcolor: alpha("#60a5fa", 0.15),
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <ComputerOutlinedIcon sx={{ color: "#60a5fa" }} />
                                                </Box>
                                                <Box>
                                                    <Typography fontWeight={800} sx={{ color: "common.white" }}>
                                                        Connected campus
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
                                                        Clear communication with families and modern tools for learning.
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                    <Button
                                        variant="contained"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => scrollToId("contact")}
                                        sx={{
                                            mt: 3,
                                            px: 3,
                                            py: 1.25,
                                            borderRadius: 999,
                                            bgcolor: "#2563eb",
                                            "&:hover": { bgcolor: "#1d4ed8" },
                                        }}
                                    >
                                        Discover more
                                    </Button>
                                </Grid>
                            </Grid>
                        </Container>
                    </Box>
                ) : null}

                <Box id="topics" sx={{ py: { xs: 8, md: 10 }, bgcolor: PAGE_BG }}>
                    <Container maxWidth="lg">
                        <Typography variant="overline" sx={{ color: "#60a5fa", letterSpacing: 2, fontWeight: 700 }}>
                            Highlights
                        </Typography>
                        <Typography variant="h3" sx={{ color: "common.white", mt: 1, mb: 4, fontSize: { xs: "1.75rem", md: "2.25rem" } }}>
                            Favourite areas to explore
                        </Typography>
                        <Grid container spacing={2.5}>
                            {topicCards.map((t, i) => (
                                <Grid item xs={12} sm={6} md={3} key={i}>
                                    <Box
                                        sx={{
                                            position: "relative",
                                            borderRadius: 3,
                                            overflow: "hidden",
                                            height: 320,
                                            cursor: "pointer",
                                            "&:hover .topic-img": { transform: "scale(1.05)" },
                                        }}
                                        onClick={() => scrollToId("contact")}
                                        role="presentation"
                                    >
                                        <Box
                                            className="topic-img"
                                            component="img"
                                            src={t.image}
                                            alt=""
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                transition: "transform 0.45s ease",
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                background: "linear-gradient(180deg, transparent 40%, rgba(10,11,26,0.92) 100%)",
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                left: 20,
                                                top: "58%",
                                                width: 48,
                                                height: 48,
                                                borderRadius: "50%",
                                                bgcolor: brandOrange,
                                                border: `4px solid ${PAGE_BG}`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <ArrowForwardIcon sx={{ color: "#fff", fontSize: 22 }} />
                                        </Box>
                                        <Box sx={{ position: "absolute", left: 20, right: 20, bottom: 20 }}>
                                            <Typography variant="h6" fontWeight={800} sx={{ color: "common.white" }}>
                                                {t.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: alpha("#fff", 0.75) }}>
                                                {t.subtitle}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>

                {(schoolPage.vision || schoolPage.mission) && (
                    <Box sx={{ py: 6, bgcolor: SURFACE }}>
                        <Container maxWidth="lg">
                            <Grid container spacing={2.5}>
                                {schoolPage.vision ? (
                                    <Grid item xs={12} md={6}>
                                        <Paper
                                            sx={{
                                                p: 3,
                                                height: "100%",
                                                bgcolor: SURFACE_ELEV,
                                                border: `1px solid ${alpha("#fff", 0.06)}`,
                                                borderTop: `4px solid ${brandOrange}`,
                                                borderRadius: 3,
                                            }}
                                        >
                                            <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: "common.white" }}>
                                                Our vision
                                            </Typography>
                                            <Typography sx={{ color: MUTED }}>{schoolPage.vision}</Typography>
                                        </Paper>
                                    </Grid>
                                ) : null}
                                {schoolPage.mission ? (
                                    <Grid item xs={12} md={6}>
                                        <Paper
                                            sx={{
                                                p: 3,
                                                height: "100%",
                                                bgcolor: SURFACE_ELEV,
                                                border: `1px solid ${alpha("#fff", 0.06)}`,
                                                borderTop: "4px solid #60a5fa",
                                                borderRadius: 3,
                                            }}
                                        >
                                            <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: "common.white" }}>
                                                Our mission
                                            </Typography>
                                            <Typography sx={{ color: MUTED }}>{schoolPage.mission}</Typography>
                                        </Paper>
                                    </Grid>
                                ) : null}
                            </Grid>
                        </Container>
                    </Box>
                )}

                {facts.length > 0 ? (
                    <Container maxWidth="lg" sx={{ py: 8 }}>
                        <Typography variant="h4" textAlign="center" fontWeight={800} sx={{ color: "common.white", mb: 4 }}>
                            School at a glance
                        </Typography>
                        <Grid container spacing={2}>
                            {facts.map((fact, idx) => (
                                <Grid item xs={6} sm={4} md={3} key={idx}>
                                    <Paper
                                        sx={{
                                            p: 2.5,
                                            textAlign: "center",
                                            bgcolor: SURFACE_ELEV,
                                            border: `1px solid ${alpha("#fff", 0.06)}`,
                                            borderRadius: 3,
                                        }}
                                    >
                                        <Typography variant="h5" fontWeight={800} sx={{ color: brandOrange }}>
                                            {fact.value}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: MUTED }}>
                                            {fact.label}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                ) : null}

                {schoolPage.showAnnouncements && notices.length > 0 ? (
                    <Box id="news" sx={{ py: 8, bgcolor: SURFACE }}>
                        <Container maxWidth="lg">
                            <Typography variant="overline" sx={{ color: "#60a5fa", letterSpacing: 2, fontWeight: 700 }}>
                                News
                            </Typography>
                            <Typography variant="h4" fontWeight={800} sx={{ color: "common.white", mb: 3 }}>
                                Notices & announcements
                            </Typography>
                            <Stack spacing={2}>
                                {notices.map((notice) => (
                                    <Paper
                                        key={notice._id || notice.id}
                                        sx={{
                                            p: 2.5,
                                            bgcolor: SURFACE_ELEV,
                                            border: `1px solid ${alpha("#fff", 0.06)}`,
                                            borderRadius: 3,
                                        }}
                                    >
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            justifyContent="space-between"
                                            spacing={1}
                                            sx={{ mb: 1 }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={800} sx={{ color: "common.white" }}>
                                                {notice.title}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: MUTED }}>
                                                {formatNoticeDate(notice.date)}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" sx={{ color: MUTED }}>
                                            {notice.details}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Stack>
                        </Container>
                    </Box>
                ) : null}

                <Box id="apply" sx={{ py: 10, bgcolor: SURFACE }}>
                    <Container maxWidth="lg">
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <Typography variant="overline" sx={{ color: brandOrange, letterSpacing: 2, fontWeight: 700 }}>
                                    Admissions
                                </Typography>
                                <Typography variant="h3" sx={{ color: "common.white", mt: 1, mb: 2, fontSize: { xs: "1.75rem", md: "2.25rem" } }}>
                                    Apply for admission
                                </Typography>
                                <Typography sx={{ color: MUTED, mb: 3, maxWidth: 560, lineHeight: 1.7 }}>
                                    New learners can apply online. The school office reviews each application and, if accepted,
                                    registers the pupil and assigns them to a class. You will be contacted after review.
                                </Typography>
                                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                    <Button
                                        component={RouterLink}
                                        to={publicSchoolApplyPath(publicSlug)}
                                        variant="contained"
                                        size="large"
                                        endIcon={<ArrowForwardIcon />}
                                        sx={{
                                            px: 3,
                                            borderRadius: 999,
                                            bgcolor: brandOrange,
                                            boxShadow: `0 12px 36px ${alpha(brandOrange, 0.35)}`,
                                            "&:hover": { bgcolor: alpha(brandOrange, 0.9) },
                                        }}
                                    >
                                        Start application
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => scrollToId("contact")}
                                        sx={{
                                            px: 3,
                                            borderRadius: 999,
                                            borderColor: alpha("#fff", 0.35),
                                            color: "common.white",
                                            "&:hover": { borderColor: "common.white", bgcolor: alpha("#fff", 0.06) },
                                        }}
                                    >
                                        Questions? Contact us
                                    </Button>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Paper
                                    sx={{
                                        p: 3,
                                        bgcolor: SURFACE_ELEV,
                                        border: `1px solid ${alpha("#fff", 0.06)}`,
                                        borderRadius: 3,
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: "common.white", mb: 2 }}>
                                        What you will need
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {[
                                            "Applicant full name and date of birth",
                                            "Guardian name and phone number",
                                            "Preferred class (optional — office assigns on acceptance)",
                                            "Previous school details (if transferring)",
                                        ].map((item) => (
                                            <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
                                                <SchoolIcon sx={{ color: brandOrange, fontSize: 20, mt: 0.25 }} />
                                                <Typography variant="body2" sx={{ color: MUTED }}>
                                                    {item}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                {schoolPage.showContact ? (
                    <Box id="contact" sx={{ py: 10, bgcolor: PAGE_BG }}>
                        <Container maxWidth="lg">
                            <Typography variant="overline" sx={{ color: "#60a5fa", letterSpacing: 2, fontWeight: 700 }}>
                                Contact
                            </Typography>
                            <Typography variant="h3" sx={{ color: "common.white", mt: 1, mb: 4, fontSize: { xs: "1.75rem", md: "2.25rem" } }}>
                                Get in touch
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Stack spacing={2.5}>
                                        {schoolPage.phone ? (
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                <PhoneIcon sx={{ color: brandOrange, mt: 0.25 }} fontSize="small" />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: MUTED }}>
                                                        Phone
                                                    </Typography>
                                                    <Typography
                                                        component="a"
                                                        href={`tel:${schoolPage.phone}`}
                                                        variant="body1"
                                                        sx={{ display: "block", color: "common.white", textDecoration: "none", fontWeight: 600 }}
                                                    >
                                                        {schoolPage.phone}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        ) : null}
                                        {schoolPage.email ? (
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                <EmailIcon sx={{ color: brandOrange, mt: 0.25 }} fontSize="small" />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: MUTED }}>
                                                        Email
                                                    </Typography>
                                                    <Typography
                                                        component="a"
                                                        href={`mailto:${schoolPage.email}`}
                                                        variant="body1"
                                                        sx={{ display: "block", color: "common.white", textDecoration: "none", fontWeight: 600 }}
                                                    >
                                                        {schoolPage.email}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        ) : null}
                                        {schoolPage.address ? (
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                <LocationOnIcon sx={{ color: brandOrange, mt: 0.25 }} fontSize="small" />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: MUTED }}>
                                                        Address
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ color: "common.white" }}>
                                                        {schoolPage.address}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        ) : null}
                                        {schoolPage.whatsappNumber ? (
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                <WhatsAppIcon sx={{ color: brandOrange, mt: 0.25 }} fontSize="small" />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: MUTED }}>
                                                        WhatsApp
                                                    </Typography>
                                                    <Typography
                                                        component="a"
                                                        href={whatsAppHref(schoolPage.whatsappNumber)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        variant="body1"
                                                        sx={{ display: "block", color: "common.white", textDecoration: "none", fontWeight: 600 }}
                                                    >
                                                        {schoolPage.whatsappNumber}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        ) : null}
                                        {schoolPage.facebookUrl ? (
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                <FacebookIcon sx={{ color: brandOrange, mt: 0.25 }} fontSize="small" />
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: MUTED }}>
                                                        Facebook
                                                    </Typography>
                                                    <Typography
                                                        component="a"
                                                        href={schoolPage.facebookUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        variant="body1"
                                                        sx={{ display: "block", color: "common.white", textDecoration: "none", fontWeight: 600 }}
                                                    >
                                                        {schoolPage.facebookUrl}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        ) : null}
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    {schoolPage.mapEmbedUrl ? (
                                        <Box
                                            sx={{
                                                borderRadius: 3,
                                                overflow: "hidden",
                                                border: `1px solid ${alpha("#fff", 0.08)}`,
                                            }}
                                        >
                                            <Box
                                                component="iframe"
                                                src={schoolPage.mapEmbedUrl}
                                                title="School location map"
                                                width="100%"
                                                height={240}
                                                sx={{ border: 0, display: "block" }}
                                            />
                                        </Box>
                                    ) : (
                                        <Paper
                                            sx={{
                                                p: 3,
                                                textAlign: "center",
                                                minHeight: 200,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: SURFACE_ELEV,
                                                border: `1px solid ${alpha("#fff", 0.06)}`,
                                                borderRadius: 3,
                                            }}
                                        >
                                            <LocationOnIcon sx={{ color: brandOrange, fontSize: 48, mb: 1 }} />
                                            <Typography variant="body1" fontWeight={700} sx={{ color: "common.white" }}>
                                                {schoolPage.address || schoolPage.schoolName}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: MUTED }}>
                                                {[schoolPage.schoolDistrict, "Malawi"].filter(Boolean).join(", ")}
                                            </Typography>
                                        </Paper>
                                    )}
                                </Grid>
                            </Grid>
                        </Container>
                    </Box>
                ) : null}

                <Box
                    sx={{
                        py: 8,
                        background: `linear-gradient(135deg, ${alpha(brandOrange, 0.95)} 0%, ${alpha("#7c3aed", 0.88)} 100%)`,
                        color: "#fff",
                    }}
                >
                    <Container maxWidth="sm" sx={{ textAlign: "center" }}>
                        <Typography variant="h5" fontWeight={800} gutterBottom>
                            Join {schoolPage.schoolName || "our school"}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.92, mb: 3 }}>
                            One login for students, teachers, and school admin — choose your role on the next screen.
                        </Typography>
                        <SchoolLoginButton
                            schoolId={schoolId}
                            size="large"
                            variant="contained"
                            sx={{
                                bgcolor: "#fff",
                                color: "#1a1d38",
                                px: 4,
                                borderRadius: 999,
                                "&:hover": { bgcolor: alpha("#fff", 0.92) },
                            }}
                        />
                    </Container>
                </Box>

                <Box sx={{ bgcolor: "#05060f", color: MUTED, py: 3 }}>
                    <Container maxWidth="lg">
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            spacing={1}
                        >
                            <Typography variant="caption" sx={{ color: alpha("#fff", 0.55) }}>
                                © {new Date().getFullYear()} {schoolPage.schoolName}
                                {isRootPlatformHome ? "" : ` · Powered by ${APP_NAME}`}
                            </Typography>
                            {(schoolPage.phone || schoolPage.email) && (
                                <Typography variant="caption" sx={{ color: alpha("#fff", 0.55) }}>
                                    {[schoolPage.phone, schoolPage.email].filter(Boolean).join(" · ")}
                                </Typography>
                            )}
                        </Stack>
                    </Container>
                </Box>
            </>
        );
    };

    let body;
    if (!routeRef) {
        body = (
            <Box sx={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
                <Paper sx={{ p: 4, maxWidth: 440, textAlign: "center", bgcolor: SURFACE_ELEV, border: `1px solid ${alpha("#fff", 0.08)}` }}>
                    <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: "common.white" }}>
                        Missing school
                    </Typography>
                    <Typography sx={{ color: MUTED, mb: 3 }}>No school was specified for this page.</Typography>
                    <Button variant="contained" onClick={() => navigate("/")} sx={{ bgcolor: brandOrange, borderRadius: 999 }}>
                        Back to home
                    </Button>
                </Paper>
            </Box>
        );
    } else if (loading && !schoolPage) {
        body = (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    bgcolor: PAGE_BG,
                }}
            >
                <CircularProgress sx={{ color: brandOrange }} />
                {schoolPage?.schoolName ? (
                    <Typography sx={{ color: MUTED }}>{schoolPage.schoolName}</Typography>
                ) : null}
            </Box>
        );
    } else if (!loading && !schoolPage) {
        body = (
            <Box sx={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", px: 2, bgcolor: PAGE_BG }}>
                <Paper sx={{ p: 4, maxWidth: 440, textAlign: "center", bgcolor: SURFACE_ELEV, border: `1px solid ${alpha("#fff", 0.08)}` }}>
                    <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: "common.white" }}>
                        School not found
                    </Typography>
                    <Typography sx={{ color: MUTED, mb: 3 }}>The school page you are looking for does not exist.</Typography>
                    <Button variant="contained" onClick={() => navigate("/")} sx={{ bgcolor: brandOrange, borderRadius: 999 }}>
                        Back to home
                    </Button>
                </Paper>
            </Box>
        );
    } else if (schoolPage && !schoolPage.isPublished && !isPreview) {
        body = (
            <>
                {renderNavbar()}
                {renderComingSoon()}
            </>
        );
    } else if (schoolPage) {
        body = (
            <>
                {isPreview && !schoolPage.isPublished ? (
                    <Alert
                        severity="info"
                        sx={{
                            borderRadius: 0,
                            justifyContent: "center",
                            bgcolor: alpha("#60a5fa", 0.12),
                            color: "common.white",
                            borderBottom: `1px solid ${alpha("#fff", 0.08)}`,
                        }}
                    >
                        Preview mode — this page is not published yet. Visitors will see a “coming soon” message until you publish.
                    </Alert>
                ) : null}
                {renderFullPage()}
            </>
        );
    } else {
        body = (
            <Box sx={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: PAGE_BG }}>
                <CircularProgress sx={{ color: brandOrange }} />
            </Box>
        );
    }

    return (
        <ThemeProvider theme={pageTheme}>
            <CssBaseline />
            <Box sx={{ minHeight: "100vh", bgcolor: PAGE_BG }}>{body}</Box>
            <PublicSchoolSubscriptionOverlay
                open={Boolean(subscriptionBlocked)}
                pageData={schoolPage}
                loginPath={staffLoginPath}
            />
        </ThemeProvider>
    );
}

export default SchoolPublicPage;
